import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import DiscountCodesEditor from "@/components/DiscountCodesEditor";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const TONE = { active: "text-emerald-700", exempt: "text-klein", grace: "text-amber-700", locked: "text-red-700" };

const naira = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 });
const digits = (v) => String(v ?? "").replace(/[^\d]/g, "");
const inputCls = "w-full border border-ink/15 px-3 py-2 text-sm focus:outline-none focus:border-klein";

function PlanRow({ p, i, set, remove, canRemove }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-[1fr_1.3fr_1fr_1.4fr_auto] gap-2 items-start border-b border-ink/5 pb-3" data-testid={`sa-plan-row-${i}`}>
            <label className="text-xs text-ink/50 md:sr-only">Code</label>
            <input value={p.id} onChange={(e) => set(i, "id", e.target.value.toLowerCase().replace(/\s+/g, "-"))} placeholder="starter" className={inputCls} data-testid={`sa-plan-id-${i}`} />
            <label className="text-xs text-ink/50 md:sr-only">Name</label>
            <input value={p.name} onChange={(e) => set(i, "name", e.target.value)} placeholder="Starter" className={inputCls} data-testid={`sa-plan-name-${i}`} />
            <label className="text-xs text-ink/50 md:sr-only">Max staff</label>
            <input inputMode="numeric" value={p.max_staff ?? ""} onChange={(e) => set(i, "max_staff", digits(e.target.value))} placeholder="Unlimited" className={inputCls} data-testid={`sa-plan-max-${i}`} />
            <label className="text-xs text-ink/50 md:sr-only">Price / year</label>
            <div>
                <input inputMode="numeric" value={p.price_ngn ?? ""} onChange={(e) => set(i, "price_ngn", digits(e.target.value))} placeholder="550000" className={inputCls} data-testid={`sa-plan-price-${i}`} />
                <div className={`mt-1 text-xs tabular-nums ${p.price_ngn !== "" && p.price_ngn != null && Number(p.price_ngn) < 100 ? "text-red-600" : "text-ink/50"}`} data-testid={`sa-plan-price-preview-${i}`}>
                    {p.price_ngn !== "" && p.price_ngn != null && Number(p.price_ngn) < 100 ? "Minimum ₦100 — Paystack refuses smaller payments" : `${naira(p.price_ngn)} per year`}
                </div>
            </div>
            <button type="button" onClick={() => remove(i)} disabled={!canRemove} className="col-span-2 md:col-span-1 p-2 text-ink/40 hover:text-red-600 disabled:opacity-30 justify-self-end" data-testid={`sa-plan-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
            </button>
        </div>
    );
}

function PlansEditor({ plans, onSaved }) {
    const [rows, setRows] = useState(plans);
    const [saving, setSaving] = useState(false);
    useEffect(() => setRows(plans), [plans]);
    const set = (i, k, v) => setRows((r) => r.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
    const remove = (i) => setRows((r) => r.filter((_, j) => j !== i));
    const add = () => setRows((r) => [...r, { id: "", name: "", max_staff: "", price_ngn: "" }]);
    const save = async () => {
        setSaving(true);
        try {
            await api.put("/super-admin/billing/plans", { plans: rows.map((p) => ({ ...p, max_staff: p.max_staff === "" ? null : p.max_staff })) });
            toast.success("Plans saved — companies see the new prices right away");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="bg-white border border-ink/10 p-6 sm:p-8" data-testid="sa-plans-editor">
            <div className="font-display font-bold text-xl">Annual plans</div>
            <p className="mt-1 text-sm text-ink/55">Companies are matched to the smallest plan that fits their active staff. Leave "Max staff" blank for unlimited (one plan must be unlimited).</p>
            <div className="mt-5 hidden md:grid grid-cols-[1fr_1.3fr_1fr_1.4fr_auto] gap-2 overline text-ink/50">
                <span>Code</span><span>Name</span><span>Max staff</span><span>Price per year (₦)</span><span className="w-8" />
            </div>
            <div className="mt-2 space-y-3">
                {rows.map((p, i) => <PlanRow key={i} p={p} i={i} set={set} remove={remove} canRemove={rows.length > 1} />)}
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
                <button type="button" onClick={add} className="inline-flex items-center gap-1.5 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid="sa-plan-add"><Plus className="w-4 h-4" /> Add plan</button>
                <button onClick={save} disabled={saving} className="bg-klein text-white px-5 py-2.5 text-sm disabled:opacity-60" data-testid="sa-plans-save">{saving ? "Saving…" : "Save plans"}</button>
            </div>
        </div>
    );
}

export default function SuperAdminBilling() {
    const [data, setData] = useState(null);
    const load = useCallback(() => api.get("/super-admin/billing").then(({ data: d }) => setData(d)).catch(() => toast.error("Could not load billing")), []);
    useEffect(() => { load(); }, [load]);

    const exception = async (c) => {
        const days = window.prompt(`Give ${c.name || c.company_id} free access for how many days? (0 removes the exception)`, "30");
        if (days === null) return;
        const note = window.prompt("Reason (optional)", c.exception_note || "") ?? "";
        try {
            await api.post(`/super-admin/billing/${c.company_id}/exception`, { days: Number(days) || 0, note });
            toast.success("Exception saved");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    if (!data) return <div className="p-10 text-ink/40">Loading…</div>;
    return (
        <div className="p-6 sm:p-10 max-w-7xl mx-auto" data-testid="sa-billing-page">
            <h1 className="font-display font-extrabold text-4xl text-ink">Billing</h1>
            <p className="mt-1 text-sm text-ink/55">New companies get 30 days, existing companies 7 days, then annual payment via Paystack.</p>
            <div className="mt-8 bg-white border border-ink/10 overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]" data-testid="sa-billing-table">
                    <thead><tr>{["Company", "Staff", "Plan needed", "Status", "Paid until", "Grace ends", "Exception", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
                    <tbody>
                        {data.companies.map((c) => (
                            <tr key={c.company_id} className="border-b border-ink/5" data-testid={`sa-billing-row-${c.company_id}`}>
                                <td className="px-4 py-3"><div className="font-medium">{c.name || c.company_id}</div><div className="text-xs text-ink/50">{c.company_id}</div></td>
                                <td className="px-4 py-3 tabular-nums">{c.staff_count}</td>
                                <td className="px-4 py-3">{c.required_plan?.name}</td>
                                <td className={`px-4 py-3 font-semibold capitalize ${TONE[c.status]}`} data-testid={`sa-billing-status-${c.company_id}`}>{c.status} {c.days_left ? `· ${c.days_left}d` : ""}</td>
                                <td className="px-4 py-3">{fmt(c.paid_until)}</td>
                                <td className="px-4 py-3">{fmt(c.grace_ends_at)}</td>
                                <td className="px-4 py-3 text-xs">{c.exception_until ? `Until ${fmt(c.exception_until)}${c.exception_note ? ` — ${c.exception_note}` : ""}` : "—"}</td>
                                <td className="px-4 py-3"><button onClick={() => exception(c)} className="border border-ink/20 px-3 py-1.5 text-xs" data-testid={`sa-billing-exception-${c.company_id}`}>Give exception</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="mt-8"><PlansEditor plans={data.plans} onSaved={load} /></div>
            <div className="mt-8"><DiscountCodesEditor /></div>
        </div>
    );
}
